class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.refreshCustomScrollbar();
    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    const recommendationsProductId = parsedState.product_id || parsedState.items?.[0]?.product_id;
    if (recommendationsProductId) {
      try {
        sessionStorage.setItem('cartDrawerRecommendationsProductId', String(recommendationsProductId));
      } catch (error) {
        this.recommendationsProductId = recommendationsProductId;
      }
    }
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshCustomScrollbar() {
    const scrollbar = initCartDrawerScrollbar(this.querySelector('.cart-drawer__items-scroll'));

    if (!scrollbar) return;
    requestAnimationFrame(scrollbar.update);
    setTimeout(scrollbar.update, 150);
  }
}

customElements.define('cart-drawer', CartDrawer);

function initCartDrawerScrollbar(wrapper) {
  if (!wrapper) return null;
  if (wrapper.__cartDrawerScrollbar) return wrapper.__cartDrawerScrollbar;

  const scroller = wrapper.querySelector('cart-drawer-items');
  const bar = wrapper.querySelector('.cart-drawer__scrollbar');
  const track = wrapper.querySelector('.cart-drawer__scrollbar-track');
  const thumb = wrapper.querySelector('.cart-drawer__scrollbar-thumb');

  if (!scroller || !bar || !track || !thumb) return null;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const maxScrollTop = () => Math.max(scroller.scrollHeight - scroller.clientHeight, 0);
  const update = () => {
    const maxScroll = maxScrollTop();
    bar.classList.toggle('is-hidden', maxScroll <= 1);
    if (maxScroll <= 1 || !track.clientHeight) return;

    const proportionalThumbHeight = (scroller.clientHeight / scroller.scrollHeight) * track.clientHeight;
    const thumbHeight = clamp(proportionalThumbHeight, 24, Math.min(track.clientHeight * 0.35, 120));
    const maxThumbTop = Math.max(track.clientHeight - thumbHeight, 0);
    const thumbTop = maxScroll ? (scroller.scrollTop / maxScroll) * maxThumbTop : 0;

    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbTop}px)`;
  };
  const scrollToThumbPosition = (clientY) => {
    const rect = track.getBoundingClientRect();
    const thumbHeight = thumb.offsetHeight;
    const maxThumbTop = Math.max(track.clientHeight - thumbHeight, 1);
    const thumbTop = clamp(clientY - rect.top - thumbHeight / 2, 0, maxThumbTop);

    scroller.scrollTop = (thumbTop / maxThumbTop) * maxScrollTop();
    update();
  };
  const onScrollButtonClick = (event) => {
    const button = event.target.closest('[data-cart-scroll]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const direction = button.dataset.cartScroll === 'up' ? -1 : 1;
    scroller.scrollTop = clamp(scroller.scrollTop + direction * Math.max(scroller.clientHeight * 0.75, 80), 0, maxScrollTop());
    update();
  };
  const onTrackClick = (event) => {
    if (event.target === thumb || event.target.closest('[data-cart-scroll]')) return;

    event.preventDefault();
    event.stopPropagation();
    scrollToThumbPosition(event.clientY);
  };
  const onThumbPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const startY = event.clientY;
    const startScrollTop = scroller.scrollTop;
    const onPointerMove = (moveEvent) => {
      moveEvent.preventDefault();

      const maxThumbTop = Math.max(track.clientHeight - thumb.offsetHeight, 1);
      const nextScrollTop = startScrollTop + (moveEvent.clientY - startY) * (maxScrollTop() / maxThumbTop);

      scroller.scrollTop = clamp(nextScrollTop, 0, maxScrollTop());
      update();
    };
    const onPointerUp = () => {
      thumb.classList.remove('is-dragging');
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };

    thumb.classList.add('is-dragging');
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  };

  scroller.addEventListener('scroll', update, { passive: true });
  bar.addEventListener('click', onScrollButtonClick);
  track.addEventListener('click', onTrackClick);
  thumb.addEventListener('pointerdown', onThumbPointerDown);

  const resizeObserver = window.ResizeObserver ? new ResizeObserver(update) : null;
  resizeObserver?.observe(scroller);
  resizeObserver?.observe(track);

  const mutationObserver = new MutationObserver(update);
  mutationObserver.observe(scroller, { childList: true, subtree: true });

  wrapper.__cartDrawerScrollbar = {
    update,
    destroy() {
      scroller.removeEventListener('scroll', update);
      bar.removeEventListener('click', onScrollButtonClick);
      track.removeEventListener('click', onTrackClick);
      thumb.removeEventListener('pointerdown', onThumbPointerDown);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      delete wrapper.__cartDrawerScrollbar;
    },
  };

  requestAnimationFrame(update);
  setTimeout(update, 150);
  return wrapper.__cartDrawerScrollbar;
}

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    this.cartDrawerScrollbar = initCartDrawerScrollbar(this.closest('.cart-drawer__items-scroll'));
  }

  disconnectedCallback() {
    this.cartDrawerScrollbar?.destroy();
    super.disconnectedCallback();
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
